import type { TOC } from '@ember/component/template-only';
import { fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import {
  PrismLoadingBar,
  PrismMessage,
  PrismModal,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { gt } from 'ember-truth-helpers';
import GeneralModalFilterBar from 'lumen/components/general/modal-filter-bar';
import ParityTablesTableParity from 'lumen/components/parity/tables/table-parity';
import type MarketParityInsightHistoricalController from 'lumen/controllers/market/parity/insight/historical';

interface MarketParityInsightHistoricalSignature {
  Args: {
    controller: MarketParityInsightHistoricalController;
    model: unknown;
  };
}

<template>
  <PrismModal
    @onClose={{@controller.redirectOnClose}}
    @title={{@controller.hotel.hotelinfo.name}}
  >
    <div class="modal--stretched flex flex-col overflow-hidden">
      {{#if (gt @controller.platformOptions.length 1)}}
        <div class="platform-filter-header">
          <PrismSelectInput
            @onChange={{@controller.setPlatform}}
            @options={{@controller.platformOptions}}
            @value={{@controller.selectedPlatformOption}}
          />
        </div>
      {{/if}}

      <GeneralModalFilterBar class="flex items-center justify-between">
        <div class="truncate">
          {{@controller.filterString}}
        </div>
        <div class="flex items-center gap-200 whitespace-nowrap">
          <div
            class="prism-link"
            data-event-id="market-parity-insight-live-view"
          >
            <LinkTo
              @disabled={{@controller.model.parityPermission.isRunning}}
              @model={{@controller.liveParityHotelId}}
              @query={{@controller.hotelViewQuery}}
              @route="hotel.parity"
              target="_blank"
              {{on
                "click"
                (fn
                  @controller.trackRedirectToLiveView
                  @controller.liveParityHotelId
                )
              }}
            >
              {{t "parity_analytics.hotels.live_link"}}
            </LinkTo>
          </div>
          {{#if @controller.exportDateOptions.length}}
            <span>-</span>
            {{#if (gt @controller.exportDateOptions.length 1)}}
              <PrismSelectInput
                @onChange={{@controller.selectExportDate}}
                @options={{@controller.exportDateOptions}}
                @value={{@controller.selectedExportDate}}
              />
            {{else}}
              <p class="text-200--regular">{{get
                  @controller.exportDateOptions
                  "0.label"
                }}</p>
            {{/if}}
          {{/if}}
        </div>
      </GeneralModalFilterBar>

      {{#if @controller.isLoading}}
        <div class="relative">
          <PrismLoadingBar />
        </div>
      {{/if}}
      {{#if @controller.hasNoExportData}}
        <div class="flex grow items-center justify-center">
          <PrismMessage
            @description={{t "general.error_short"}}
            @illustration="100x100_warning"
            @title={{t "general.error_title"}}
          />
        </div>
      {{else if @controller.showTable}}
        <div
          class="grow overflow-auto"
          data-event-id="market-parity-insight-hotel-detail-table"
        >
          <ParityTablesTableParity
            @availableOTAs={{@controller.availableOTAs}}
            @hasDayLiveShop={{false}}
            @hotel={{@controller.hotel}}
            @isLoading={{@controller.isLoading}}
            @isModal={{true}}
            @isParityAnalytics={{true}}
            {{! @glint-expect-error: Incorrect type }}
            @message={{@controller.noDataForSelection}}
            @periods={{@controller.model.parities.last.value}}
            @showMemberRate={{@controller.showMemberRate}}
            @showMeta={{@controller.showMeta}}
          />
        </div>
      {{/if}}
    </div>
  </PrismModal>
</template> satisfies TOC<MarketParityInsightHistoricalSignature>;
